import * as Sentry from '@sentry/react';
import type { AuthMeResponse } from 'api/auth/Auth.schemas.ts';
import { AxiosError } from 'axios';
import { GET } from 'common/helpers.ts';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';
import { actions } from '#admin/reducer/app.ts';
import type { RootState } from '#admin/store.ts';

// Hook for cases where we need to handle undefined (e.g., auth gates, non-authenticated contexts)
export const useMeSuspense = (): { me?: AuthMeResponse; mutate: () => void } => {
  const token = useSelector((state: RootState) => state.app.token);
  const dispatch = useDispatch();

  // Only use suspense when we have a token to avoid throwing on unauthenticated routes
  const {
    data: me,
    error,
    mutate,
  } = useSWR<AuthMeResponse>(token ? ['/auth/me', token] : null, GET, {
    dedupingInterval: 30_000,
    revalidateOnFocus: false,
    suspense: !!token, // Only suspend when authenticated
  });

  React.useEffect(() => {
    Sentry.setUser(me || null);
    if (me && !me.isActive) {
      dispatch(actions.logout());
    }
  }, [me, dispatch]);

  React.useEffect(() => {
    if (error) {
      if (error instanceof AxiosError && error.response?.status === 503) return;
      dispatch(actions.logout());
    }
  }, [dispatch, error]);

  return { me, mutate };
};

// Main hook for authenticated routes where we know me will never be undefined due to Suspense
export const useMe = (): { me: AuthMeResponse; mutate: () => void } => {
  const { me, mutate } = useMeSuspense();
  // This should never happen due to Suspense, but we'll throw an error to catch any misuse during development
  if (!me) throw new Error('useMe() called in unauthenticated context. Use useMeSuspense() instead.');
  return { me, mutate };
};
